import { useState } from "react"
import { Col, Div, Row, Text, Textarea } from "atomize"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import Chip from "../atoms/Chip"
import PtrLevelPicker from "../lesson/PtrLevelPicker"
import { theme } from "../settings/config"
import { editPlayerTechnicalSheet } from "../utils/firestore.utils"
import { getPlayerFullName } from "../utils/func.utils"
import FocusAreasPicker from "../lesson/FocusAreasPicker"
import { PLAYER_STYLES } from "../utils/lessonContent.utils"
import { FOCUS_NOTE_MAX_TEXT, normalizeTechnicalSheet } from "../utils/technicalSheet.utils"

// Un riquadro bianco per domanda, sul fondo caldo del foglio
const Box = ({ children }) => (
	<Div
		bg={theme.colors.surface}
		rounded="20px"
		p="0.875rem 1rem"
		m={{ b: "0.75rem" }}
		d="flex"
		flexDir="column">
		{children}
	</Div>
)

// La scheda tecnica dell'allievo: le stesse pastiglie PTR del CAD, con la
// guida, e le stesse macroaree del contenuto della lezione. Ogni macroarea
// scelta ha la sua nota, facoltativa; niente voti.
export function TechnicalSheetModal({ closeModal, playerData }) {
	const [sheet, setSheet] = useState(() => normalizeTechnicalSheet(playerData.technicalSheet))
	const [isSaving, setSaving] = useState(false)

	const update = changes => setSheet(current => ({ ...current, ...changes }))

	// la nota di una macroarea spenta resta qui finché il foglio è aperto: se
	// la si riaccende per sbaglio torna com'era. Non si salva: lo dice
	// normalize.
	const setNote = (id, text) =>
		setSheet(current => ({
			...current,
			notes: { ...current.notes, [id]: text.slice(0, FOCUS_NOTE_MAX_TEXT) },
		}))

	const handleSave = async () => {
		setSaving(true)
		try {
			await editPlayerTechnicalSheet(playerData.playerID, sheet)
			closeModal()
		} catch {
			toast.error("La scheda tecnica non è stata salvata", { position: "top-center" })
			setSaving(false)
		}
	}

	return (
		<>
			<Container>
				<Div textWeight="bold" textSize="title">
					Scheda tecnica
				</Div>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
					{getPlayerFullName(playerData)}
				</Text>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					<Box>
						<PtrLevelPicker
							title="Livello PTR:"
							value={sheet.ptrLevel}
							onChange={ptrLevel => update({ ptrLevel })}
						/>
					</Box>

					<Box>
						<Paragraph title="Tipo di giocatore:" m={{ b: "0.5rem" }} />
						<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
							{PLAYER_STYLES.map(style => (
								<Chip
									key={style.id}
									text={style.label}
									color={theme.colors.primary}
									isSelected={sheet.playerStyle === style.id}
									disabled={isSaving}
									// scelta singola: toccare quello scelto lo toglie
									onClick={() =>
										update({
											playerStyle:
												sheet.playerStyle === style.id ? null : style.id,
										})
									}
								/>
							))}
						</Div>
					</Box>

					<Box>
						<Paragraph title="Su cosa lavora:" m={{ b: "0.5rem" }} />
						<FocusAreasPicker
							value={sheet}
							onChange={update}
							disabled={isSaving}
							// nel riquadro bianco: i blocchi caldi, le voci bianche
							blockBackground={theme.colors.background}
							itemBackground={theme.colors.surface}
							renderExtra={area => (
								<Textarea
									aria-label={`Nota su ${area.label.toLowerCase()}`}
									placeholder="Nota (facoltativa)"
									bg={theme.colors.surface}
									// un appunto: due righe bastano, poi scorre
									h="4.25rem"
									p="0.5rem 0.75rem"
									border="3px solid"
									borderColor={theme.colors.surface}
									focusBorderColor={theme.colors.gunmetal}
									rounded="12px"
									textSize="caption"
									value={sheet.notes[area.id] ?? ""}
									disabled={isSaving}
									onChange={e => setNote(area.id, e.target.value)}
								/>
							)}
						/>
					</Box>
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text="Annulla"
							textSize="subheader"
							disabled={isSaving}
							action={closeModal}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={isSaving ? "Salvo..." : "Salva"}
							disabled={isSaving}
							action={handleSave}
						/>
					</Col>
				</Row>
			</Div>
		</>
	)
}

export default TechnicalSheetModal
